import { useState, useCallback } from "react";
import { useConfirmationModal } from "@imminently/react/context/modals";
import { Text } from "@imminently/react/ui/core/text";

type UseRecordEditorOptions<T extends { id: string }> = {
  /** Function to update the records array */
  setRecords: React.Dispatch<React.SetStateAction<T[]>>;
  /** Field name to display in delete confirmation (defaults to "id") */
  displayField?: keyof T;
  /** Custom delete confirmation message */
  deleteMessage?: (record: T) => React.ReactNode;
};

type UseRecordEditorReturn<T extends { id: string }> = {
  /** Currently editing record (null if not editing) */
  editingRecord: T | null;
  /** Start editing a record */
  handleEdit: (record: T) => void;
  /** Save the edited record */
  handleEditSave: (updatedRecord: T) => void;
  /** Cancel editing */
  handleEditCancel: () => void;
  /** Delete a record with confirmation */
  handleDelete: (record: T) => void;
};

/**
 * Hook for handling edit and delete operations on records.
 * Provides state management for editing and confirmation dialogs for deletion.
 */
export const useRecordEditor = <T extends { id: string }>({
  setRecords,
  displayField = "id" as keyof T,
  deleteMessage,
}: UseRecordEditorOptions<T>): UseRecordEditorReturn<T> => {
  const confirmation = useConfirmationModal();
  const [editingRecord, setEditingRecord] = useState<T | null>(null);

  const handleEdit = useCallback((record: T) => {
    setEditingRecord(record);
  }, []);

  const handleEditSave = useCallback((updatedRecord: T) => {
    setRecords((prev) =>
      prev.map((item) => (item.id === updatedRecord.id ? updatedRecord : item))
    );
    setEditingRecord(null);
  }, [setRecords]);

  const handleEditCancel = useCallback(() => {
    setEditingRecord(null);
  }, []);

  const handleDelete = useCallback((record: T) => {
    const displayValue = String(record[displayField]);
    const message = deleteMessage
      ? deleteMessage(record)
      : <Text>Are you sure you want to delete <strong>{displayValue}</strong>?</Text>;

    confirmation({
      title: "Confirm delete",
      message,
      confirmProps: {
        text: "Delete",
        color: "error"
      },
      cancelProps: {
        text: "Cancel",
        variant: "text",
      }
    }).then(() => {
      setRecords((prev) => prev.filter((item) => item.id !== record.id));
    }).catch(() => {
      // do nothing
    });
  }, [confirmation, setRecords, displayField, deleteMessage]);

  return {
    editingRecord,
    handleEdit,
    handleEditSave,
    handleEditCancel,
    handleDelete,
  };
};

